import { faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, DialogContent, Grid, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";
import { EmployeeFilter } from "src/models/employees/employee-filter";
import { AnomalyLastNotificationAndSignoffDTO } from "src/models/tna/tna-anomalies/anomaly-last-notification-signoff/anomaly-last-notification-signoff-dto";
import { TnADayDataDTO } from "src/models/tna/tna-data/tna-day-data-dto";
import { TnADayDataWithBreaksRef } from "src/models/tna/tna-day-data-with-breaks";
import AddEditButton from "src/reusable-components/AddEditButton/AddEditButton";
import ConcatenatedCell from "src/reusable-components/ConcatenatedCell/ConcatenatedCell";
import { AnomalyLastNotificationAndSignoff } from "src/services/tna-anomalies/anomaly-last-notification-signoff-api.service";
import { intlFormatDateShort, intlFormatDateFullMonth } from "src/utils/time-utils";
import MultipleEmployeesAnomaliesHistoryTable from "./MultipleEmployeesAnomaliesHistoryTable";
import MultipleEmployeesAnomaliesListTable from "./MultipleEmployeesAnomaliesListTable";
import SingleEmployeeAnomaliesHistoryTable from "./SingleEmployeeAnomaliesHistoryTable";
import SingleEmployeeAnomaliesListTable from "./SingleEmployeeAnomaliesListTable";
import useWindowSize from "../../../../custom-hooks/useWindowSize/useWindowSize";
import DialogTitle from "../../../DialogTitle/DialogTitle";

interface Props {
  item?: TnADayDataWithBreaksRef | TnADayDataDTO;
  handleNotifyParent: () => void;
  children?: React.ReactNode;
  buttonText?: string;
  buttonVariant?: "text" | "outlined" | "contained";
  customButtonStyle?: React.CSSProperties;
  buttonTitle?: string;
  disabled?: boolean;
  isModalOpen?: boolean;
  handleParentClose?: () => void;
}

export interface State {
  lastNotifiedOn: string;
  lastSignOffOn: string;
  loading: boolean;
  error: string;
  isOpen: boolean;
  isHistoryView: boolean;
}

const SendAnomaliesNotificationsWrapper = ({
  item,
  handleNotifyParent,
  children,
  buttonText,
  buttonVariant,
  customButtonStyle,
  buttonTitle,
  disabled = false,
  isModalOpen,
  handleParentClose,
}: Props) => {
  const tRP = "components.my_team_breaks";
  const { t } = useTranslation();
  const theme = useTheme();

  const [state, setState] = useState<State>({
    lastNotifiedOn: "",
    lastSignOffOn: "",
    loading: false,
    error: "",
    isOpen: false,
    isHistoryView: false,
  });

  const { height } = useWindowSize();

  const changeView = useCallback((isHistoryView: boolean) => {
    setState((state) => ({
      ...state,
      isHistoryView,
    }));
  }, []);

  const handleNotify = useCallback(() => {
    setState((state) => ({ ...state, isOpen: false }));
    handleNotifyParent();
    // if (isMutated.current) {
    //   handleNotifyParent();
    // }
  }, [handleNotifyParent]);

  const handleClose = useCallback(() => {
    setState((state) => ({ ...state, isHistoryView: false, isOpen: false }));
  }, []);

  // add unauthorized view
  // const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  //#region load lastNotifiedOn and lastSignOffOn
  const loadItems = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setState((prev) => ({ ...prev, loading: true }));

        const propsFilter = { employeeId: item?.employee?.id } as EmployeeFilter;

        const response = await new AnomalyLastNotificationAndSignoff().loadItems(
          propsFilter,
          undefined, // query
          undefined, // sort
          undefined,
          undefined, // exactMatch
          60, // timeout
          signal
        );

        if (!signal?.aborted && response.items) {
          const anomalyItems = response.items as AnomalyLastNotificationAndSignoffDTO[];

          setState((prev) => ({
            ...prev,
            lastNotifiedOn: anomalyItems[0]?.lastNotifiedOn,
            lastSignOffOn: anomalyItems[0]?.lastSignOffOn,
            loading: false,
          }));
        }
      } catch (error) {
        if (!signal?.aborted) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: error as string,
          }));
          console.error("Error loading anomaly items:", error);
        }
      }
    },
    [item?.employee?.id]
  );
  //#endregion

  useEffect(() => {
    const abortController = new AbortController();

    loadItems(abortController.signal);

    return () => abortController.abort();
  }, []);

  if (isModalOpen !== undefined) {
    return (
      <LoadingOverlay loading={state.loading || height === 0}>
        <Grid>
          {/* <Grid style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <Typography
              variant="body2"
              color={!state.isHistoryView ? theme.palette.primary.main : theme.palette.text.secondary}
            >
              {t(`${tRP}.anomalies_list_view`)}
            </Typography>
            <Switch
              checked={state.isHistoryView}
              onChange={(_, checked) =>
                setState((prev) => ({
                  ...prev,
                  isHistoryView: checked,
                }))
              }
              size="small"
            />
            <Typography
              variant="body2"
              color={state.isHistoryView ? theme.palette.primary.main : theme.palette.text.secondary}
            >
              {t(`${tRP}.anomalies_history_view`)}
            </Typography>
          </Grid> */}
          {/* {state.isHistoryView ? (
            <MultipleEmployeesAnomaliesHistoryTable
              handleNotifyParent={handleNotifyParent}
              tableHeight={height - height / 3}
              handleClose={handleParentClose}
            />
          ) : ( */}
          {height !== 0 && (
            <MultipleEmployeesAnomaliesListTable
              handleNotifyParent={handleNotifyParent}
              tableHeight={height - height / 2.5}
              handleClose={handleParentClose}
            />
          )}
          {/* )} */}
        </Grid>
      </LoadingOverlay>
    );
  }

  return (
    <>
      <AddEditButton
        title={
          buttonTitle ?? buttonText ?? item ? t(`${tRP}.show_employee_anomalies`) : t(`${tRP}.show_employees_anomalies`)
        }
        effect={"edit"}
        onClick={() => {
          setState((state) => ({ ...state, isOpen: true }));
        }}
        {...(!children && buttonText && { buttonText })}
        {...(buttonVariant && { buttonVariant })}
        {...(customButtonStyle && { customStyle: customButtonStyle })}
        {...(disabled && { disabled })}
      >
        {children}
      </AddEditButton>

      <Dialog
        open={state.isOpen}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              maxWidth: "80% !important",
              // maxHeight: "90% !important",
            },
          },
          "& .MuiDialogActions-root": {
            padding: "0 !important",
          },
          // "& .MuiGrid-root:first-of-type": {
          //   width: "100% !important",
          // },
        }}
      >
        {state.isOpen && (
          <>
            <DialogTitle
              title={
                item && state.isHistoryView
                  ? `${t(`${tRP}.employee_presence_anomalies`)} - ${t(`${tRP}.history_of_notifications`)}`
                  : state.isHistoryView
                  ? `${t(`${tRP}.employees_presence_anomalies`)} - ${t(`${tRP}.history_of_notifications`)}`
                  : item
                  ? `${t(`${tRP}.employee_presence_anomalies`)}${
                      item.tnADate
                        ? ` on ${intlFormatDateFullMonth(
                            new Date(item.tnADate.year, item.tnADate.month - 1, item.tnADate.day)
                          )}`
                        : ""
                    }`
                  : `${t(`${tRP}.employees_presence_anomalies`)}`
              }
              handleClose={handleClose}
              additionalSection={
                <Button
                  variant="contained"
                  onClick={() => changeView(!state.isHistoryView)}
                  style={{
                    textTransform: "none",
                    marginTop: "5px",
                    padding: "5px",
                    width: "fit-content",
                  }}
                >
                  <Typography variant="body2">
                    {state.isHistoryView
                      ? t(`${tRP}.change_to_anomalies_list_view`)
                      : t(`${tRP}.change_to_anomalies_history_view`)}
                  </Typography>
                </Button>
              }
            />
            <LoadingOverlay loading={state.loading || height === 0}>
              <DialogContent>
                {item?.employee?.id && (
                  <Grid
                    container
                    sx={{ alignItems: "center", marginBottom: "10px" }}
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={6}
                    >
                      <ConcatenatedCell
                        aligned={"left"}
                        FirstElement={
                          <Grid>
                            <Typography>{item.employee?.name}</Typography>
                            {item.employee?.birthDate && (
                              <Typography variant={"body2"}>
                                <FontAwesomeIcon
                                  icon={faBirthdayCake}
                                  size="xs"
                                />{" "}
                                &nbsp;
                                {intlFormatDateShort(item.employee?.birthDate)}
                              </Typography>
                            )}
                          </Grid>
                        }
                        SecondElement={
                          <Grid>
                            <Typography
                              style={{ color: theme.palette.text.disabled }}
                              variant={"body2"}
                              title={item.employee?.legalEntity?.name}
                            >
                              {item.employee?.legalEntity?.code ?? item.employee?.legalEntity?.name}
                            </Typography>
                          </Grid>
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{ textAlign: "right", display: "flex", flexDirection: "column", gap: 0.5 }}
                    >
                      <Typography variant="body2">
                        {t("common_words.last_anomaly_notification_sent_on")}
                        {": "}
                        {state.lastNotifiedOn ? intlFormatDateShort(new Date(state.lastNotifiedOn)) : "-"}
                      </Typography>
                      <Typography variant="body2">
                        {t("common_words.last_signoff_date")}
                        {": "}
                        {state.lastSignOffOn ? intlFormatDateShort(new Date(state.lastSignOffOn)) : "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                {height !== 0 && (
                  <>
                    {state.isHistoryView && item?.employee ? (
                      <SingleEmployeeAnomaliesHistoryTable
                        employee={item?.employee}
                        handleNotifyParent={handleNotify}
                        tableHeight={height - height / 2.5}
                        handleClose={handleClose}
                        days={item?.tnADate ? [item?.tnADate] : []}
                      />
                    ) : state.isHistoryView ? (
                      <MultipleEmployeesAnomaliesHistoryTable
                        handleNotifyParent={handleNotify}
                        tableHeight={height - height / 2.5}
                        handleClose={handleClose}
                      />
                    ) : item?.employee ? (
                      <SingleEmployeeAnomaliesListTable
                        employee={item?.employee}
                        handleNotifyParent={handleNotify}
                        tableHeight={height - height / 2.5}
                        handleClose={handleClose}
                        days={item?.tnADate ? [item?.tnADate] : []}
                      />
                    ) : (
                      <MultipleEmployeesAnomaliesListTable
                        handleNotifyParent={handleNotify}
                        tableHeight={height - height / 2.5}
                        handleClose={handleClose}
                      />
                    )}
                  </>
                )}
              </DialogContent>
            </LoadingOverlay>
          </>
        )}
      </Dialog>
    </>
  );
};

export default React.memo(SendAnomaliesNotificationsWrapper);
